/* ORION motion across the site (orion-motion.css has the rest). The owner's rule: all of it plays also when the
   system asks for reduced motion. */

// 1. Page to page. A press on a catalogue picture answers at once: an exact copy of the photograph (without the card's
//    padding) lifts a little over it and the other cards step back, while the product page loads. As the page changes
//    (a same-site view transition, @view-transition in orion-motion.css) the copy takes the name orion-product-picture
//    and flies straight to its place in the product page's gallery (the pagereveal script in theme.liquid), which
//    moves it with a transform only, so it stays smooth while the new page is busy.
(() => {
  const KEY = 'orion-flying-picture';
  let pressed = null;
  const restore = () => {
    if (!pressed) return;
    clearTimeout(pressed.timer);
    pressed.copy.remove();
    pressed.picture.style.visibility = '';
    pressed.card.classList.remove('is-chosen');
    document.documentElement.classList.remove('orion-leaving');
    pressed = null;
  };
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    const card = link ? link.closest('.orion-collection-page__card, [data-orion-catalog-card]') : null;
    const picture = card ? card.querySelector('.orion-collection-page__card-media > img') : null;
    if (!picture || !picture.complete || !picture.naturalWidth || link.target === '_blank') return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
    restore();
    // Where the photograph itself is: the image box less its padding (as it is drawn, hover scale included), then
    // the part object-fit: contain fills.
    const box = picture.getBoundingClientRect();
    const style = getComputedStyle(picture);
    const k = box.width / (picture.offsetWidth || box.width);
    const pad = ['Left', 'Top', 'Right', 'Bottom'].map(side => (parseFloat(style[`padding${side}`]) || 0) * k);
    const innerW = box.width - pad[0] - pad[2], innerH = box.height - pad[1] - pad[3];
    const ratio = picture.naturalWidth / picture.naturalHeight;
    const width = Math.min(innerW, innerH * ratio), height = width / ratio;
    const copy = new Image();
    copy.src = picture.currentSrc || picture.src;
    copy.alt = '';
    copy.setAttribute('aria-hidden', 'true');
    copy.className = 'orion-flying-picture';
    Object.assign(copy.style, {
      left: `${box.left + pad[0] + (innerW - width) / 2 + scrollX}px`, top: `${box.top + pad[1] + (innerH - height) / 2 + scrollY}px`,
      width: `${width}px`, height: `${height}px`
    });
    document.body.appendChild(copy);
    picture.style.visibility = 'hidden';
    card.classList.add('is-chosen');
    document.documentElement.classList.add('orion-leaving');
    copy.animate([{ transform: 'none' }, { transform: 'scale(1.03)' }], { duration: 420, easing: 'cubic-bezier(.22,.72,.2,1)', fill: 'forwards' });
    // If the product page never comes (a slow network gave up, the visitor stayed), the catalogue comes back.
    pressed = { picture, card, copy, path: url.pathname, timer: setTimeout(restore, 12000) };
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ path: url.pathname, src: copy.src, ratio, at: Date.now() }));
    } catch (error) { /* Private mode: the page still cross-fades. */ }
  }, true);
  window.addEventListener('pageswap', event => {
    if (!event.viewTransition) return;
    // A skipped transition (for instance a hidden tab) just navigates; no error in the console.
    event.viewTransition.ready.catch(() => {});
    if (!pressed) return;
    const destination = event.activation && event.activation.entry && event.activation.entry.url;
    if (!destination || new URL(destination).pathname !== pressed.path) return;
    pressed.copy.style.viewTransitionName = 'orion-product-picture';
    // Where the flight starts, for the product page to set its path.
    const box = pressed.copy.getBoundingClientRect();
    try {
      const data = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (data) sessionStorage.setItem(KEY, JSON.stringify({ ...data, from: { x: box.left, y: box.top, w: box.width, h: box.height } }));
    } catch (error) { /* The picture then flies on the browser's own path. */ }
  });
  // Back on this page from the cache: the picture sits in its card again.
  window.addEventListener('pageshow', restore);
})();

// 2. A price that changes (size, quantity) rolls to the new figure: each digit that changes rises into place, as the
//    gallery's counter does. Only plain figures roll; a price written with markup simply changes.
(() => {
  const PRICES = [
    '[data-orion-price]', '[data-orion-order-price]', '[data-orion-mobile-price]',
    '[data-orion-topper-price]', '[data-orion-topper-order-price]', '[data-orion-topper-mobile-price]',
    '[data-orion-pillow-price]', '[data-orion-pillow-order-price]', '[data-orion-pillow-mobile-price]',
    '[data-orion-duvet-price]', '[data-orion-duvet-order-price]', '[data-orion-duvet-mobile-price]',
    '[data-orion-coating-price]', '[data-orion-coating-order-price]', '[data-orion-coating-mobile-price]',
    '[data-orion-bed-price]', '[data-orion-bed-order-price]', '[data-orion-bed-mobile-price]',
    '[data-orion-metal-price]', '[data-rp-price]'
  ].join(',');
  if (!Element.prototype.animate || !('MutationObserver' in window)) return;
  const shown = new WeakMap();
  const roll = (element, before, after) => {
    if (element.children.length) return false;
    const old = [...before], next = [...after];
    const offset = next.length - old.length;
    const fragment = document.createDocumentFragment();
    const moving = [];
    next.forEach((character, index) => {
      const span = document.createElement('span');
      span.className = 'orion-roll';
      span.textContent = character;
      fragment.append(span);
      if (/\d/.test(character) && old[index - offset] !== character) moving.push(span);
    });
    if (!moving.length) return false;
    element.replaceChildren(fragment);
    moving.forEach((span, index) => span.animate(
      [{ opacity: 0, transform: 'translateY(45%)' }, { opacity: 1, transform: 'translateY(0)' }],
      { duration: 420, delay: index * 35, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'backwards' }
    ));
    return true;
  };
  const observer = new MutationObserver(records => {
    const touched = new Set();
    records.forEach(record => {
      const node = record.target.nodeType === 1 ? record.target : record.target.parentElement;
      const price = node && node.closest(PRICES);
      if (price) touched.add(price);
    });
    touched.forEach(price => {
      const text = price.textContent.trim();
      const before = shown.get(price);
      shown.set(price, text);
      if (before === undefined || before === text) return;
      if (roll(price, before, text)) observer.takeRecords();
    });
  });
  const watch = () => document.querySelectorAll(PRICES).forEach(price => {
    if (shown.has(price)) return;
    shown.set(price, price.textContent.trim());
    observer.observe(price, { childList: true, characterData: true, subtree: true });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch, { once: true });
  else watch();
  document.addEventListener('shopify:section:load', watch);
})();

// 3. Catalogues: after a switch of series, filters or order, the cards rise into place one after another (the drift
//    of the gallery's own reveal) and the count runs to its new figure.
(() => {
  if (!Element.prototype.animate) return;
  const ease = t => 1 - Math.pow(1 - t, 3);
  const count = (label, before) => {
    if (!label) return;
    const after = label.textContent;
    const from = (before.match(/\d+/g) || []).map(Number), to = (after.match(/\d+/g) || []).map(Number);
    if (!to.length || from.length !== to.length || from.every((value, index) => value === to[index])) return;
    const start = performance.now(), duration = 650;
    const frame = now => {
      if (!label.isConnected) return;
      const t = Math.min(1, (now - start) / duration);
      let index = 0;
      label.textContent = t < 1 ? after.replace(/\d+/g, () => { const value = Math.round(from[index] + (to[index] - from[index]) * ease(t)); index += 1; return String(value); }) : after;
      if (t < 1) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  };
  const bind = root => {
    if (root.dataset.orionCascade === 'true') return;
    root.dataset.orionCascade = 'true';
    let before = '';
    root.addEventListener('orion:collection-results-before', () => {
      before = root.querySelector('.orion-collection-page__toolbar-meta>span')?.textContent || '';
    });
    root.addEventListener('orion:collection-results-updated', () => requestAnimationFrame(() => {
      [...root.querySelectorAll('[data-orion-product-grid] > li')]
        .filter(card => { const box = card.getBoundingClientRect(); return box.bottom > 0 && box.top < innerHeight; })
        .slice(0, 12)
        .forEach((card, index) => card.animate(
          [{ opacity: 0, transform: 'translate3d(0,16px,0)' }, { opacity: 1, transform: 'none' }],
          { duration: 640, delay: index * 55, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'backwards' }
        ));
      count(root.querySelector('.orion-collection-page__toolbar-meta>span'), before);
    }));
  };
  const init = () => document.querySelectorAll('[data-orion-gallery]').forEach(bind);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
